import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import type { DataTableColumnDef } from '@bliks-io/ui';
import type { SystemWithCapacity } from '@bliks-io/contracts';
import { useUserTombstoneLookup } from '@/api/queries/useOrgMembers';
import { useAuthStore } from '@/stores/auth-store';
import type { EntityListColumnArgs } from '@/components/entity-list/EntityListPage';
import {
  createSystemLogoColumn,
  createSystemNameColumn,
  createSystemOwnerColumn,
  createSystemCategoryColumn,
  createSystemCapacityColumn,
  createSystemLicenceSeatsColumn,
  createSystemActionsColumn
} from './system-columns';

export function useSystemListColumns({
  rows,
  openEdit,
  openDelete
}: EntityListColumnArgs<SystemWithCapacity>): DataTableColumnDef<SystemWithCapacity>[] {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const navigate = useNavigate();
  const userLookup = useUserTombstoneLookup();

  const handleView = useCallback(
    (system: SystemWithCapacity) => {
      navigate({ to: '/$slug/systems/$systemId', params: { slug, systemId: system._id } });
    },
    [navigate, slug]
  );

  const showLicenceSeats = rows.some((s) => s.licenceSeats !== undefined && s.licenceSeats > 0);
  const showCapacity = rows.some((s) => s.capabilities?.canViewSensitiveFields);

  return useMemo(() => {
    const cols = [
      createSystemLogoColumn(t),
      createSystemNameColumn(slug, t),
      createSystemOwnerColumn(t, userLookup),
      createSystemCategoryColumn(t)
    ];
    if (showLicenceSeats) {
      cols.push(createSystemLicenceSeatsColumn(t));
    }
    if (showCapacity) {
      cols.push(createSystemCapacityColumn(t));
    }
    cols.push(
      createSystemActionsColumn(
        { canView: true, canEdit: true, canDelete: true },
        { onView: handleView, onEdit: openEdit, onDelete: openDelete }
      )
    );
    return cols;
  }, [t, slug, userLookup, showLicenceSeats, showCapacity, handleView, openEdit, openDelete]);
}
